import React, { useEffect, useRef, useState } from 'react'

import {
  fetchBrightcoveData,
  getVideoResourceUrl
} from '../../lib/fetchBrightcoveData'
import { formatText, formatTime } from '../../lib/formatters'
import { VideoPlayer } from '../VideoPlayer/VideoPlayer'
import { VideoPlayerProvider } from '../VideoPlayer/VideoPlayerProvider'
import { VideoThumbnail } from '../VideoThumbnail/VideoThumbnail'
import type { VideoPlayerInlineProps } from './VideoPlayerInline.types'
import './video-player-inline.scss'
import xss from 'xss'

type PlaylistVideo = {
  id: string
  name?: string
  long_description?: string
  duration?: number
  poster?: string
}

export const VideoPlayerInline: React.FC<VideoPlayerInlineProps> = ({
  accountId,
  policyKey,
  playlistId,
  testId,
  previewOnly = false
}) => {
  const [videos, setVideos] = useState<PlaylistVideo[]>([])
  const [activeVideoId, setActiveVideoId] = useState<string | null>(null)
  const fetchedRef = useRef(false)

  useEffect(() => {
    if (fetchedRef.current) return
    fetchedRef.current = true

    fetchBrightcoveData<{ videos?: PlaylistVideo[] }>(
      accountId,
      policyKey,
      getVideoResourceUrl(undefined, playlistId)
    )
      .then(data => {
        if (data.videos) {
          setVideos(data.videos)
        }
      })
      .catch(err => console.error('VideoPlayerInline fetch error', err))
  }, [accountId, policyKey, playlistId])

  if (videos.length === 0) return null

  const activate = (videoId: string) => {
    if (previewOnly) return
    setActiveVideoId(videoId)
  }

  return (
    <VideoPlayerProvider>
      <div className="VideoPlayerInline" data-testid={testId || undefined}>
        {videos.map(video => {
          const isActive = activeVideoId === video.id

          return (
            <div
              key={video.id}
              className={`VideoPlayerInline-item${isActive ? ' VideoPlayerInline-item--active' : ''}`}
            >
              {isActive ? (
                <div className="VideoPlayerInline-playerWrapper">
                  <VideoPlayer
                    accountId={accountId}
                    policyKey={policyKey}
                    videoId={video.id}
                    autoplay
                    deferLoad={false}
                  />
                  <div className="VideoPlayerInline-activeContent Content Content--open">
                    {video.name && (
                      <h4
                        className="VideoPlayerInline-heading u-textSecondary u-textSizeMsrp4"
                        dangerouslySetInnerHTML={{
                          __html: xss(formatText(video.name))
                        }}
                      />
                    )}
                    {video.long_description && (
                      <p className="VideoPlayerInline-description">
                        <span
                          dangerouslySetInnerHTML={{
                            __html: xss(formatText(video.long_description))
                          }}
                        />{' '}
                        <span className="VideoPlayerInline-duration">
                          {formatTime(video.duration)}
                        </span>
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="VideoPlayerInline-row">
                  <div className="VideoPlayerInline-thumbnail">
                    <VideoThumbnail
                      accountId={accountId}
                      policyKey={policyKey}
                      videoId={video.id}
                      posterUrl={video.poster}
                      onActivate={() => activate(video.id)}
                      previewOnly={previewOnly}
                    />
                  </div>
                  <div
                    className="VideoPlayerInline-content Content Content--open"
                    onClick={() => activate(video.id)}
                    role="presentation"
                  >
                    {video.name && (
                      <h4
                        className="VideoPlayerInline-heading u-textSecondary u-textSizeMsrp4"
                        dangerouslySetInnerHTML={{
                          __html: xss(formatText(video.name))
                        }}
                      />
                    )}
                    {video.long_description && (
                      <p className="VideoPlayerInline-description">
                        <span
                          dangerouslySetInnerHTML={{
                            __html: xss(formatText(video.long_description))
                          }}
                        />{' '}
                        <span className="VideoPlayerInline-duration">
                          {formatTime(video.duration)}
                        </span>
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </VideoPlayerProvider>
  )
}
